'use client'

import { useState } from 'react'
import { ArrowUpRight, Check, Copy } from '@/components/ui/icons'
import type { Flux3Clip } from '@/lib/flux3/tests.generated'

/** Settings read as machine facts, so they're set as compact mono chips. */
function Chip({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 rounded-md border border-white/10 bg-white/[0.03] px-2 py-1 font-mono text-[11px] leading-none">
      <span className="text-white/35">{label}</span>
      <span className="text-white/85">{value}</span>
    </span>
  )
}

export function PromptPanel({ clip }: { clip: Flux3Clip }) {
  const [copied, setCopied] = useState(false)
  const [open, setOpen] = useState(false)

  const copy = async () => {
    if (!clip.prompt) return
    try {
      await navigator.clipboard.writeText(clip.prompt)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard can be blocked; the text is selectable regardless.
    }
  }

  const s = clip.settings
  const chips = [
    { label: 'model', value: s.model },
    { label: 'dur', value: s.seconds ? `${Math.round(s.seconds)}s` : '—' },
    { label: 'ar', value: s.aspect ?? '—' },
    ...(s.resolution ? [{ label: 'res', value: s.resolution }] : []),
    { label: 'render', value: s.quality },
    { label: 'chars', value: clip.promptChars.toLocaleString() },
  ]

  const lines = (clip.prompt ?? '').split('\n')

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {chips.map(c => (
          <Chip key={c.label} {...c} />
        ))}
      </div>

      {clip.prompt ? (
        <div className="mt-3 overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0b]">
          <div className="flex items-center justify-between gap-3 border-b border-white/10 px-3 py-2">
            <button
              type="button"
              onClick={() => setOpen(o => !o)}
              className="font-mono text-[11px] text-white/45 transition-colors hover:text-white/80"
              aria-expanded={open}
            >
              {open ? '▾' : '▸'} prompt.txt
            </button>
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-1.5 font-mono text-[11px] text-white/45 transition-colors hover:text-white"
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? 'copied' : 'copy'}
            </button>
          </div>

          {/* Always in the DOM — collapsed only visually, so crawlers and agents
              still read every prompt on the page. */}
          <div className={open ? 'max-h-[30rem] overflow-auto' : 'max-h-28 overflow-hidden'}>
            <pre className="relative grid grid-cols-[2.5rem_1fr] gap-x-3 p-3 font-mono text-[11.5px] leading-[1.7] text-white/60">
              {lines.map((line, i) => (
                <span key={i} className="contents">
                  <span className="select-none text-end text-white/20">{i + 1}</span>
                  <span className="whitespace-pre-wrap break-words">{line || ' '}</span>
                </span>
              ))}
            </pre>
          </div>

          {!open ? (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="w-full border-t border-white/10 bg-gradient-to-t from-black/60 to-transparent py-2 font-mono text-[11px] text-white/40 transition-colors hover:text-white/80"
            >
              show all {lines.length} lines
            </button>
          ) : null}
        </div>
      ) : null}

      <a
        href={clip.shotUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-take-link={clip.title}
        className="group mt-2.5 inline-flex items-center gap-1.5 font-mono text-[11px] text-white/40 transition-colors hover:text-white"
      >
        open on canvas
        <ArrowUpRight
          strokeWidth={1.5}
          className="h-3 w-3 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
        />
      </a>
    </div>
  )
}
